import { useGetAllCoursesLite, useGetMentorQualityScores, useGetUserProfile } from '@actions';
import { ReactComponent as BackSignIcon } from '@assets/icons/back-sign.svg';
import Button from '@components/V4/Button';
import Modal from '@components/V4/Modal/Modal';
import QualityScoreBadge from '@components/V4/QualityScore/QualityScoreBadge';
import { getUserPills } from '@modules/Admin/utils/helpers';
import { GetAllCoursesLiteDTO } from '@shared/clientTypes/DTOs/CoursesDTO';
import { UserProfileDTO } from '@shared/clientTypes/DTOs/UserDTO';
import { createContext } from 'react';
import DetailsSectionPills from '../DetailsSectionPills';
import MentorDetailsSection from './MentorDetailsSection/MentorDetailsSection';
import MentorTabsSection, { MentorTab } from './MentorTabsSection/MentorTabsSection';

interface MentorDetailsPageProps {
  mentorId: string;
  tab?: MentorTab;
  isBackButtonActive?: boolean;
  onGoBack?: () => void;
  onEnrollmentModalOpen: (id: string) => void;
  onSessionModalOpen: (id: string) => void;
  onStudentModalOpen: (id: string) => void;
}

interface MentorDetailsPageContext {
  mentor?: Partial<UserProfileDTO>;
  courses?: GetAllCoursesLiteDTO[];
  onEnrollmentModalOpen: (id: string) => void;
  onSessionModalOpen: (id: string) => void;
  onStudentModalOpen: (id: string) => void;
}

export const MentorDetailsPageContext = createContext<MentorDetailsPageContext>({
  onEnrollmentModalOpen: () => console.warn('onEnrollmentModalOpen not implemented'),
  onSessionModalOpen: () => console.warn('onSessionModalOpen not implemented'),
  onStudentModalOpen: () => console.warn('onStudentModalOpen not implemented'),
});

const MentorDetailsPage = (props: MentorDetailsPageProps) => {
  const {
    mentorId,
    tab,
    isBackButtonActive = false,
    onGoBack,
    onEnrollmentModalOpen,
    onSessionModalOpen,
    onStudentModalOpen,
  } = props;
  const { data: mentor } = useGetUserProfile(mentorId);
  const { data: courses } = useGetAllCoursesLite();
  const { data: qualityScores } = useGetMentorQualityScores({ mentorId }, { enabled: !!mentorId });
  const qualitySummary = qualityScores?.[0];

  return (
    <>
      <Modal.Header className="relative font-raleway text-xl font-bold text-center mb-6">
        {isBackButtonActive && (
          <Button
            icon={<BackSignIcon />}
            className="absolute top-0 left-6"
            variant="secondary"
            onClick={onGoBack}
            buttonText="Back"
          />
        )}
        <h3>Mentor Profile</h3>
        <h3>{mentor?.fullName}</h3>
        <DetailsSectionPills pills={getUserPills(mentor)} />
        <div className="mt-3 flex items-center justify-center gap-2 text-sm font-normal">
          <span className="text-customGrey">Average AI score:</span>
          <QualityScoreBadge
            score={qualitySummary?.average}
            size="large"
            emptyText="No graded sessions"
            suffix={
              qualitySummary ? (
                <span className="font-normal opacity-80">
                  · {qualitySummary.graded} {qualitySummary.graded === 1 ? 'session' : 'sessions'}
                </span>
              ) : null
            }
          />
        </div>
      </Modal.Header>
      <Modal.Body>
        <MentorDetailsPageContext.Provider
          value={{
            mentor,
            courses,
            onEnrollmentModalOpen,
            onSessionModalOpen,
            onStudentModalOpen,
          }}
        >
          {mentor && <MentorDetailsSection />}
          <MentorTabsSection tab={tab} />
        </MentorDetailsPageContext.Provider>
      </Modal.Body>
    </>
  );
};

export default MentorDetailsPage;
